import { Button, ClickAwayListener, Popper, PopperPlacementType } from '@mui/material';
import * as React from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import { useOpen } from 'common/hooks/open';
import fonts from 'styles/typography.module.css';
import { useStyles } from './styles';

/**
 * We use the React type here because its abillity to take a function AND the
 * boolean primitive is very useful for when we need to toggle the value
 */
type RequestOpen = React.Dispatch<React.SetStateAction<boolean>>;

interface Props {
  title: string;
  count: number;
  clearFilter: () => void;
  children: JSX.Element;
  isOpen?: boolean;
  onRequestOpen?: RequestOpen;
  placement?: PopperPlacementType;
  disabled?: boolean;
}

export const FilterButton: React.FC<Props> = ({
  title,
  count,
  clearFilter,
  children,
  isOpen,
  onRequestOpen,
  placement,
  disabled,
}) => {
  const { anchorElRef, setOpen, open } = useOpen(isOpen, onRequestOpen);
  const [arrowRef, setArrowRef] = React.useState<HTMLSpanElement | null>(null);
  const classes = useStyles();

  const handleOnClickClearFilter = React.useCallback(() => {
    clearFilter();
    setOpen(false);
  }, [clearFilter, setOpen]);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className="relative">
        <div
          className={`px-2 flex items-center justify-items-center ${count && 'bg-blue-100 px-4'} ${
            !disabled && 'hover:underline'
          }`}
        >
          {count > 0 && (
            <CloseIcon
              fontSize="small"
              className="cursor-pointer text-blue-900"
              onClick={handleOnClickClearFilter}
            />
          )}
          <Button
            endIcon={<ArrowDropDownIcon />}
            onClick={() => setOpen((o) => !disabled && !o)}
            ref={anchorElRef}
            disabled={disabled}
            sx={{
              '&.MuiButtonBase-root:hover': {
                bgcolor: 'transparent',
              },
            }}
            className={`first:pl-0 ${count > 0 || isOpen ? 'text-blue-900' : 'text-gray-700'}`}
          >
            {title}
          </Button>
        </div>
        <Popper
          open={open}
          placement={placement}
          anchorEl={anchorElRef.current}
          style={{ zIndex: 1110 }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'arrow',
              enabled: true,
              options: {
                element: arrowRef,
              },
            },
            { name: 'flip', options: { flipVariations: false, fallbackPlacements: ['bottom'] } },
          ]}
          className="shadow-md"
        >
          <span className={classes.arrow} ref={setArrowRef} />
          <div
            className={`py-2 bg-white border border-blue-900 rounded-sm relative min-w-60 ${fonts.font}`}
          >
            {children}
          </div>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};
FilterButton.displayName = 'ContainerFilters.FilterButton';
